import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, of, Subject } from 'rxjs';
import { filter, first, switchMap, take } from 'rxjs/operators';
import { ApiService } from '../api.service';
import { AppState } from '../app.state';
import { WhatFixService } from '../components/whatfix/whatfix.service';
import { loadInitialData } from '../store/actions/app.actions';
import { Role } from '../types/role';
import { AuthService } from './auth.service';

@Injectable()
export class AuthGuardService {
  initializedRoles: boolean = false;
  needsAdminRole: boolean = false;
  locationsLoadedEvent$: Subject<boolean> = new Subject<boolean>();

  constructor(
    public auth: AuthService,
    public router: Router,
    private apiService: ApiService,
    private whatFix: WhatFixService,
    private store: Store<AppState>
  ) { }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    this.needsAdminRole = route.data?.needsAdminRole ?? false;

    return this.auth.isReady$.pipe(
      filter(ready => ready === true),  // wait until SDK is ready
      take(1),
      switchMap(() => {
        return this.auth.isAuthenticated$.pipe(
          take(1),
          switchMap(isAuthenticated => {
            if (!isAuthenticated) {
              // User is not authenticated, redirect to login
              this.auth.loginRedirect({
                appState: { returnTo: state.url }
              });
              return of(false);
            }
            
            return this.checkRoles(state);
          })
        );
      })
    );
  }

  private checkRoles(state: RouterStateSnapshot): Observable<boolean> {
    if (!this.initializedRoles) {
      this.store.dispatch(loadInitialData());
      this.initializedRoles = true;
    }

    return new Observable<boolean>((observer) => {
      this.store
        .select(s => s.locationList)
        .pipe(filter(l => !!l.locations), first())
        .subscribe(_ => {
          if (this.needsAdminRole && !this.auth.hasRole(Role.Admin)) {
            this.router.navigate(['']);
            observer.next(false);
          }
          // Allow access to /no-locations for authenticated users with no TRS roles
          else if (state.url.indexOf('/no-locations') !== -1) {
            observer.next(true);
          }
          else if (!this.auth.hasRole(Role.User) && !this.auth.hasRole(Role.ReadonlyUser)) {
            this.router.navigate(['no-locations']);
            observer.next(false);
          }
          else if (state.url.indexOf('/add-request') !== -1 && !this.auth.hasRole(Role.User)) {
            this.router.navigate(['']);
            observer.next(false);
          }
          else {
            if (!this.whatFix.isInjected) {
              this.whatFix.initWhatFix();
            }

            if (!this.auth.loggedIn) {
              this.auth.loggedInEvent$.next(true);
            }

            this.locationsLoadedEvent$.next(true);

            observer.next(true);
          }

          observer.complete();
        });
    });
  }
}

export const AuthGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> | boolean => {
  return inject(AuthGuardService).canActivate(route, state);
}